import React from "react";
import { Info, X, CheckCircle, AlertCircle } from "lucide-react";

export type ToastType = "success" | "error" | "info";

interface ToastNotificationProps {
  message: string | null;
  onClose: () => void;
  type?: ToastType;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  message,
  onClose,
  type = "info",
}) => {
  if (!message) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-white" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-white" />;
      default:
        return <Info className="h-5 w-5 text-white" />;
    }
  };

  const getGradient = () => {
    switch (type) {
      case "success":
        return "from-green-500 to-green-600";
      case "error":
        return "from-red-500 to-red-600";
      default:
        return "from-blue-500 to-blue-600";
    }
  };

  const getBarGradient = () => {
    switch (type) {
      case "success":
        return "from-green-500 via-green-400 to-green-500";
      case "error":
        return "from-red-500 via-red-400 to-red-500";
      default:
        return "from-blue-500 via-blue-400 to-blue-500";
    }
  };

  return (
    <div className="fixed bottom-[190px] right-4 z-[9999] animate-[slideIn_0.3s_cubic-bezier(0.16,1,0.3,1)]">
      <div className="relative flex items-start gap-3 bg-white/95 backdrop-blur-md border border-blue-200/60 rounded-md shadow-[0_8px_30px_rgb(0,0,0,0.12)] max-w-md min-w-[300px] p-4 transition-all duration-300 hover:shadow-[0_10px_40px_rgb(0,0,0,0.15)] hover:scale-[1.02]">
        <div
          className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getBarGradient()} rounded-t-xl`}
        />

        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${getGradient()} flex items-center justify-center shadow-lg animate-[pulse_2s_ease-in-out_infinite]`}
        >
          {getIcon()}
        </div>

        <div className="flex-1 pt-0.5 min-w-0 max-h-40 overflow-y-auto pr-1">
          <p className="text-gray-800 text-[13px] leading-[1.6] font-medium tracking-wide break-words whitespace-pre-wrap">
            {message}
          </p>
        </div>

        <button
          onClick={onClose}
          className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-all duration-200 hover:rotate-90"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
